import { Linking, StyleSheet, Text } from 'react-native'
import { useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { router } from 'expo-router'
import * as CardApplication from '@/src/experiences/card-application'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { DesignSystem } from '@/src/design-system'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { CashForMortgageStore } from '../statsig/store'
import { useLogError } from '@/src/common/hooks/useLogError'
import { PointsToCashForMortgageGql } from '../gql'
import { randomUUID } from '@/src/common/utils/randomUUID'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Button } from '@/src/common/components/Button'

function RedemptionConfirmation() {
  const {
    pointsToRedeem,
    remainingPoints,
    creditAmount,
    conversionRate,
    destinationAccount,
    idempotencyKey,
    mortgageAddress,
  } = CashForMortgageStore.useCashForMortgage()
  const Alert = useAlert(RedemptionConfirmation.name)
  const logError = useLogError()

  const cardPolicyAndProcedures =
    CardApplication.Statsig.ParameterStores.useCardPolicyAndProcedures()

  const [redeemPointsAsCashForMortgage, { loading: redeemPointsLoading }] = useMutation(
    PointsToCashForMortgageGql.Mutations.REDEEM_POINTS_FOR_MORTGAGE_TRANSFER,
  )

  const handleContinue = () => {
    void redeemPointsAsCashForMortgage({
      variables: {
        numPoints: Number(pointsToRedeem),
        plaidAccountId: destinationAccount?.plaidAccountId ?? '',
        idempotencyKey: idempotencyKey ?? randomUUID(),
        mortgageAddressId: mortgageAddress?.id ?? '',
      },
      onCompleted() {
        router.push({
          pathname: '/main/points-to-cash-for-mortgage/success-confirmation',
        })
      },
      onError(error) {
        logError(error)
        Alert.alert(
          'Oops, Something Went Wrong!',
          `We couldn't process your points redemption at this time. Please try again later, or contact support if the issue persists.`,
          [
            {
              text: 'Contact Support',
              onPress: () => {
                const email = 'mailto:support@mesamember.com'

                void Linking.openURL(email)
              },
              style: 'default',
            },
            {
              text: 'Return to home',
              onPress: router.dismissAll,
              style: 'default',
            },
          ],
        )
        return
      },
    })
  }

  useEffect(() => {
    if (
      !pointsToRedeem ||
      !remainingPoints ||
      !creditAmount ||
      !conversionRate ||
      !destinationAccount ||
      !idempotencyKey ||
      !mortgageAddress
    ) {
      logError(new Error('Missing required data for redemption confirmation'), {
        pointsToRedeem,
        remainingPoints,
        creditAmount,
        idempotencyKey,
        mortgageAddress,
      })
    }
  }, [
    pointsToRedeem,
    remainingPoints,
    creditAmount,
    conversionRate,
    destinationAccount,
    idempotencyKey,
    mortgageAddress,
    logError,
  ])

  return (
    <StandaloneWrapper
      screen="/main/points-to-cash-for-mortgage/redemption-confirmation"
      title="Confirm Redemption"
      profileProps={{ menuIconProps: { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] } }}
      hideBackButton={false}
    >
      <DesignSystem.PointsRedemption.PointsRedemptionConfirmationInfoCard
        pointsToRedeem={pointsToRedeem ? parseInt(pointsToRedeem) : 0}
        remainingPoints={remainingPoints ? parseInt(remainingPoints) : 0}
        creditAmount={creditAmount ? parseInt(creditAmount) : 0}
        conversionRate={conversionRate ?? ''}
      />

      <DesignSystem.Card style={styles.whiteBlock}>
        <Text style={typography['label/sm-regular']}>
          Typically your transfer will be processed within 24 hours however it may take up to 3
          business days based upon the date and time of scheduling.
        </Text>
        <Text style={typography['label/sm-regular']}>
          Points redemption for this benefit is final once submitted and cannot be reversed.
        </Text>
        <Text
          style={styles.termsText}
          onPress={() =>
            void openInAppBrowser(
              cardPolicyAndProcedures.get(
                'cardholder_rewards_terms_of_use',
                'https://mesamember.com/legal/mesa-membership-terms-and-conditions',
              ),
              'Terms and Conditions',
            )
          }
        >
          Terms and conditions
        </Text>
      </DesignSystem.Card>

      <Button
        title="Use Points"
        loading={redeemPointsLoading}
        onPress={handleContinue}
        disabled={redeemPointsLoading || remainingPoints === 'loading'}
      />
    </StandaloneWrapper>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
    paddingHorizontal: 14,
    gap: 32,
    color: mesaColorNames['global/grey/80 #2F3133'],
  },
  whiteBlock: {
    borderRadius: 12,
    padding: 16,
    gap: 24,
  },
  infoContainer: {
    gap: 16,
  },
  infoSection: {
    gap: 8,
    padding: 4,
  },
  infoSectionBorder: {
    borderBottomWidth: 1,
    borderBottomColor: mesaColorNames['semantic/bg/disabled #DADDDE'],
    paddingBottom: 16,
  },
  infoHeader: {
    ...typography['label/sm-regular'],
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
  termsText: {
    ...typography['label/sm-regular'],
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
})

export const RedemptionConfirmationScreen = {
  Screen: RedemptionConfirmation,
  Name: 'redemption-confirmation',
}
